/**
 * In-call invites for deaf-to-deaf group calls: who was asked, and what the
 * server said about each of them. Shared by the web panel
 * (components/web/InvitePanel.web.tsx) and the native one
 * (components/InvitePanel.native.tsx).
 *
 * A row's status is only ever what the server confirmed. The row used to say
 * "Ringing…" the moment the button was pressed and never changed, whether
 * the person was found, rang, answered, declined or did not exist.
 *
 * Server contract (session/p2p-group-review 7f0a4c6ff): conference_add
 * { callId, clientId | handle | email | phoneNumber } is answered with
 * conference_invite_sent { callId, inviteId, target: { clientId, name },
 * requested, ringing, expiresAt } or error { code, message, operation:
 * 'conference_add', callId }, then conference_invite_result { callId,
 * inviteId, targetId, name, status: accepted | declined | missed |
 * cancelled }, which a group caller also gets for each first-round ring.
 * Replies carry no request id, so a reply is matched to its row by what was
 * asked for (requested), then by who it resolved to (target), and failing
 * both to the oldest row still waiting for one.
 *
 * A ring still ringing can be stopped by whoever rang it, or by the host:
 * conference_invite_cancel { callId, targetId }, answered with a
 * conference_invite_result 'cancelled' or an error with operation
 * 'conference_invite_cancel' (vrs-server ws/p2p-handlers.js).
 */
import { contactsListResponseSchema } from '../../../contracts/api';
import { MAX_CALL_PARTICIPANTS, inviteTarget, participantKeys, sameParticipant } from '../../../shared/call-capacity';
/**
 * Window event the room pill's Invite button dispatches in platform rooms
 * (vrs-room-controls.js) to open or close the React invite panel.
 */
export const ROOM_INVITE_TOGGLE_EVENT = 'malka:room-invite-toggle';
/** How long a row waits for conference_invite_sent before saying so. */
export const INVITE_CONFIRM_TIMEOUT_MS = 15000;
/** How long a Cancel waits for the server before saying it was not confirmed. */
export const INVITE_CANCEL_TIMEOUT_MS = 10000;
/** The typed field's entry as an invite target: an @handle or an email. */
export function typedInviteTarget(text) {
    const address = inviteTarget(text);
    if (!address) {
        return null;
    }
    return 'handle' in address ? { handle: address.handle } : { email: address.email };
}
/** A saved contact as an invite target: its linked account, else its number. */
export function contactInviteTarget(contact) {
    if (contact.linkedClientId) {
        return { clientId: contact.linkedClientId };
    }
    return contact.phoneNumber ? { phoneNumber: contact.phoneNumber } : null;
}
/**
 * /api/contacts rows are database rows (snake_case, nulls for empty
 * columns). The panels read camelCase only, so "Ask to Join" sent neither an
 * account nor a number.
 */
export function toInviteContact(raw) {
    const text = (...keys) => {
        for (const key of keys) {
            if (typeof raw[key] === 'string' && raw[key].trim()) {
                return raw[key].trim();
            }
        }
        return undefined;
    };
    const handle = text('handle', 'public_handle', 'publicHandle');
    return {
        email: text('email'),
        handle: handle ? `@${handle.replace(/^@+/, '')}` : undefined,
        id: text('id') || '',
        linkedClientId: text('linkedClientId', 'linked_client_id'),
        name: text('name'),
        phoneNumber: text('phoneNumber', 'phone_number')
    };
}
/**
 * The people a GET /api/contacts answer offers to ask in, or null when the
 * answer is not a contact list. Rows nobody can be rung by (no account, no
 * number) are left out.
 */
export function inviteContactsFromResponse(data) {
    const parsed = contactsListResponseSchema.safeParse(data);
    if (!parsed.success) {
        return null;
    }
    return (Array.isArray(parsed.data) ? parsed.data : parsed.data.contacts)
        .map(item => toInviteContact(item))
        .filter(item => item.id && contactInviteTarget(item));
}
function identityOf(target) {
    if (typeof target === 'string') {
        return { target };
    }
    if (!target || typeof target !== 'object') {
        return {};
    }
    const value = target;
    const text = (key) => (typeof value[key] === 'string' ? value[key] : undefined);
    return {
        clientId: text('clientId'),
        email: text('email'),
        handle: text('handle'),
        phoneNumber: text('phoneNumber'),
        target: text('target')
    };
}
/** One key per person, so asking the same person twice updates one row. */
export function inviteRowKey(target) {
    return participantKeys(identityOf(target))[0] || JSON.stringify(target);
}
function rowForReply(rows, ...echoes) {
    const waiting = rows.filter(row => row.status === 'sending');
    for (const echo of echoes) {
        const echoed = identityOf(echo);
        const row = participantKeys(echoed).length
            ? waiting.find(item => sameParticipant(identityOf(item.target), echoed))
            : undefined;
        if (row) {
            return row;
        }
    }
    return waiting[0];
}
/** The row for an account the server named (targetId): by who it resolved to, or by its key. */
function rowForAccount(rows, targetId) {
    if (!targetId) {
        return undefined;
    }
    const key = inviteRowKey({ clientId: targetId });
    return rows.find(item => item.targetId === targetId) || rows.find(item => item.key === key);
}
function replace(rows, row, next) {
    return row ? rows.map(item => (item === row ? { ...item, ...next } : item)) : rows;
}
function resolvedAccount(target) {
    const clientId = target && typeof target === 'object' ? target.clientId : undefined;
    return typeof clientId === 'string' && clientId ? clientId : undefined;
}
/** An entry the server could not resolve, as a target that can be asked for again. */
function unresolvedTarget(entry) {
    const text = typeof entry === 'string' ? entry.trim() : '';
    if (!text) {
        return null;
    }
    const typed = typedInviteTarget(text);
    if (typed) {
        return { label: text, target: typed };
    }
    return text.replace(/\D/g, '').length >= 7 ? { label: text, target: { phoneNumber: text } } : null;
}
/**
 * A group call's first round (p2p_ringing): the people being rung, the
 * people on Do Not Disturb (never rung) and the entries that matched no
 * account. Seeds never replace a row the panel already holds.
 */
function firstRoundRows(action) {
    const person = (peer, next) => {
        if (!peer?.id) {
            return null;
        }
        const target = { clientId: peer.id };
        return { key: inviteRowKey(target), label: peer.name?.trim() || 'Someone', target, targetId: peer.id, ...next };
    };
    const rows = [
        ...(action.invited || []).map(peer => person(peer, { ringing: true, status: 'sent' })),
        ...(action.dnd || []).map(peer => person(peer, { code: 'TARGET_DND', status: 'failed' })),
        ...(action.unresolved || []).map(entry => {
            const unresolved = unresolvedTarget(entry);
            return unresolved ? { ...unresolved, code: 'NOT_FOUND', key: inviteRowKey(unresolved.target), status: 'failed' } : null;
        })
    ];
    return rows.filter((row) => Boolean(row));
}
export function inviteRowsReducer(rows, action) {
    switch (action.type) {
        case 'sending': {
            const key = inviteRowKey(action.target);
            const row = { key, label: action.label, status: 'sending', target: action.target };
            return rows.some(item => item.key === key)
                ? rows.map(item => (item.key === key ? row : item))
                : [...rows, row];
        }
        case 'sent': {
            const row = rowForReply(rows, action.requested, action.target);
            // The account the handle or email turned out to be, by name.
            const label = action.name?.trim() && row && !row.label.includes(action.name.trim())
                ? `${action.name.trim()} (${row.label})` : row?.label;
            return replace(rows, row, { cancelCode: undefined, cancelling: false, inviteId: action.inviteId, label,
                ringing: action.ringing === true, status: 'sent', targetId: resolvedAccount(action.target) || row?.targetId });
        }
        case 'result': {
            const row = (action.inviteId ? rows.find(item => item.inviteId === action.inviteId) : undefined)
                || rowForAccount(rows, action.targetId);
            if (!action.status) {
                return rows;
            }
            if (row) {
                return replace(rows, row, { cancelCode: undefined, cancelling: false, ringing: false, status: action.status,
                    targetId: row.targetId || action.targetId });
            }
            // A group caller also hears how each first-round ring ended. Someone
            // who declined or did not answer gets a row, so they can be asked again.
            if (action.targetId && action.status !== 'accepted') {
                const target = { clientId: action.targetId };
                const key = inviteRowKey(target);
                const next = { inviteId: action.inviteId, key, label: action.name?.trim() || 'Someone',
                    status: action.status, target, targetId: action.targetId };
                return rows.some(item => item.key === key)
                    ? rows.map(item => (item.key === key ? next : item))
                    : [...rows, next];
            }
            return rows;
        }
        case 'error':
            return replace(rows, rowForReply(rows, action.target), { code: action.code, status: 'failed' });
        case 'unconfirmed':
            return rows.map(item => (item.key === action.key && item.status === 'sending'
                ? { ...item, status: 'unconfirmed' } : item));
        case 'ringing': {
            const seeded = firstRoundRows(action);
            return [...rows, ...seeded.filter(row => !rows.some(item => item.key === row.key))];
        }
        case 'cancelling':
            return rows.map(item => (item.key === action.key && canCancel(item)
                ? { ...item, cancelCode: undefined, cancelling: true } : item));
        case 'cancelFailed': {
            const row = action.key ? rows.find(item => item.key === action.key) : rowForAccount(rows, action.targetId);
            // NOT_RINGING: the ring ended another way, and its own result says how.
            return row?.cancelling
                ? replace(rows, row, { cancelCode: action.code === 'NOT_RINGING' ? undefined : action.code || 'INTERNAL_ERROR',
                    cancelling: false })
                : rows;
        }
        case 'reset':
            return [];
    }
    return rows;
}
const FINAL_ERROR_CODES = ['ALREADY_IN_CALL', 'CALL_ENDED', 'NOT_PARTICIPANT'];
/** Whether "Ask again" is offered: the last attempt is over. */
export function canAskAgain(row) {
    return row.status === 'declined' || row.status === 'missed' || row.status === 'cancelled'
        || row.status === 'unconfirmed'
        || (row.status === 'failed' && !FINAL_ERROR_CODES.includes(row.code || ''));
}
/** Whether Cancel is offered: the server said this person is being rung now. */
export function canCancel(row) {
    return row.status === 'sent' && row.ringing === true && Boolean(row.targetId) && !row.cancelling;
}
/** Why a Cancel did not go through; the ring may still be going. */
export function cancelFailureText(code) {
    switch (code) {
        case 'NOT_CONNECTED':
            return 'You’re offline, so they’re still being rung.';
        case 'NOT_INVITER':
            return 'Only the person who rang them, or the host, can cancel.';
        case 'TIMEOUT':
            return 'Cancel not confirmed. Try again.';
        default:
            return 'Couldn’t cancel. Try again.';
    }
}
/** What the row says, in the person's terms. */
export function inviteStatusText(row) {
    switch (row.status) {
        case 'sending':
            return 'Sending…';
        case 'sent': {
            if (row.cancelling) {
                return 'Cancelling…';
            }
            // "Ringing" only because the server said so (ringing: true).
            const status = row.ringing ? 'Ringing…' : 'Invite sent';
            return row.cancelCode ? `${status} ${cancelFailureText(row.cancelCode)}` : status;
        }
        case 'accepted':
            return 'Accepted';
        case 'declined':
            return 'Declined';
        case 'missed':
            return 'No answer';
        case 'cancelled':
            return 'Cancelled';
        case 'unconfirmed':
            return 'Not confirmed';
        case 'failed':
            switch (row.code) {
                case 'CALL_FULL':
                    return 'Call is full';
                case 'NOT_FOUND':
                    return row.target.email ? 'No account with that email' : 'Not found';
                case 'BLOCKED':
                case 'INVITE_UNAVAILABLE':
                    return 'Can’t be invited';
                case 'TARGET_DND':
                    return 'Do Not Disturb is on';
                case 'RATE_LIMITED':
                    return 'Too many invites. Try again in a minute';
                case 'ALREADY_IN_CALL':
                    return 'Already in a call';
                case 'CALL_ENDED':
                    return 'This call has ended';
                case 'NOT_PARTICIPANT':
                    return 'You’re not in this call';
                default:
                    return 'Couldn’t invite';
            }
    }
    return '';
}
// A native group caller enters the room on p2p_ringing, before the room's
// invite panel exists: the first round is kept here, per call, for the panel
// to start from. Memory only; the last few calls.
const firstRounds = new Map();
/**
 * Keep a group call's first round (p2p_ringing) for its room's invite panel.
 *
 * @param {Object} data - The p2p_ringing payload.
 * @returns {void}
 */
export function rememberGroupRinging(data) {
    if (!data?.callId || data.groupCall !== true) {
        return;
    }
    firstRounds.delete(data.callId);
    firstRounds.set(data.callId, { dnd: data.dnd, invited: data.invited, type: 'ringing', unresolved: data.unresolved });
    while (firstRounds.size > 4) {
        firstRounds.delete(firstRounds.keys().next().value);
    }
}
/**
 * The first round kept for this call, as a reducer action, if any.
 *
 * @param {string} callId - The call.
 * @returns {Object|undefined}
 */
export function groupRingingFor(callId) {
    return callId ? firstRounds.get(callId) : undefined;
}
function listNames(names) {
    return names.length <= 2 ? names.join(' and ') : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
}
/**
 * Who a group call is NOT ringing, in one sentence: entries the server could
 * not resolve (unknown, private or blocked look the same on purpose) and
 * people with Do Not Disturb on. Empty when everyone is being rung. The web
 * ringing bar says the same (client-profile.js describeRingGaps).
 *
 * @param {Object} data - The p2p_ringing (or p2p_target_offline) payload.
 * @returns {string}
 */
export function groupRingGaps(data) {
    const unresolvedEntries = data?.unresolved;
    const dndEntries = data?.dnd;
    const unresolved = (Array.isArray(unresolvedEntries) ? unresolvedEntries : [])
        .map(entry => (typeof entry === 'string' ? entry : '').trim())
        .filter((value, index, all) => value && all.indexOf(value) === index);
    const dnd = (Array.isArray(dndEntries) ? dndEntries : [])
        .map(entry => (entry && typeof entry === 'object' && typeof entry.name === 'string'
        ? entry.name : '').trim())
        .filter(Boolean);
    return [
        unresolved.length ? `${listNames(unresolved)} couldn’t be reached.` : '',
        dnd.length ? `${listNames(dnd)} ${dnd.length === 1 ? 'has' : 'have'} Do Not Disturb on.` : ''
    ].filter(Boolean).join(' ');
}
/**
 * How a group call nobody answered ended (p2p_group_no_answer): everyone
 * declined, or someone let it ring out.
 *
 * @param {Object} data - The p2p_group_no_answer payload.
 * @returns {string}
 */
export function groupNoAnswerText(data) {
    const declined = data?.declined?.length || 0;
    const missed = data?.missed?.length || 0;
    return declined && !missed ? 'Everyone declined your call.' : 'No one answered.';
}
/** "5 of 8 in call" — everyone in the room, you included. */
export function callCapacityText(participantCount) {
    return `${participantCount} of ${MAX_CALL_PARTICIPANTS} in call`;
}
/** Deaf-to-deaf rooms are minted `p2p-` (direct) or `group-` (group call). */
export function isPlatformCallRoomName(roomName) {
    return typeof roomName === 'string' && /^(p2p|group)-/.test(roomName);
}
/**
 * A call_ended for a group participant's own departure names them
 * (participantId). Anyone else receiving such a record keeps their call:
 * one person leaving is not the end of the room.
 */
export function isOtherParticipantDeparture(data, localUserId) {
    const departed = typeof data?.participantId === 'string' ? data.participantId : '';
    return Boolean(departed) && departed !== localUserId;
}
/** Brief room notices: "Dana joined", "Dana rejoined", "Dana left", "Dana was removed". */
export function participantNoticeTitle(kind, name, detail = {}) {
    const who = name?.trim() || 'Someone';
    if (kind === 'joined') {
        return `${who} ${detail.rejoined ? 'rejoined' : 'joined'}`;
    }
    return detail.removed ? `${who} was removed` : `${who} left`;
}
/**
 * What a decline says in the room. A direct call with nobody else there is
 * over; in a group call one decline changes nothing for everyone else.
 */
export function declineNotice(name, groupRoom, othersPresent) {
    if (!groupRoom) {
        return {
            description: 'You can hang up and try again later.',
            title: `${name || 'The other party'} declined the call`
        };
    }
    return {
        description: othersPresent ? 'The call continues.' : undefined,
        title: `${name || 'Someone'} declined`
    };
}
